'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '@/lib/api';

type MemberCategory = { id: string; name: string };

export default function NameSearchBox() {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const initialQ = useMemo(() => sp.get('q') ?? '', [sp]);
  const initialCategoryId = useMemo(() => sp.get('categoryId') ?? '', [sp]);
  const initialSort = useMemo(() => sp.get('sort') ?? 'recent', [sp]);
  const initialGender = useMemo(() => sp.get('gender') ?? '', [sp]);

  const [q, setQ] = useState(initialQ);
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [sort, setSort] = useState(initialSort);
  const [gender, setGender] = useState(initialGender);

  const [categories, setCategories] = useState<MemberCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  useEffect(() => {
    setQ(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ]);

  useEffect(() => {
    setCategoryId(initialCategoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryId]);

  useEffect(() => {
    setSort(initialSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSort]);

  useEffect(() => {
    setGender(initialGender);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGender]);

  const setParam = useCallback(
    (params: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(sp.toString());
      Object.entries(params).forEach(([k, v]) => {
        next.delete(k);
        if (v == null || v === '') return;
        next.set(k, v);
      });
      router.push(`${pathname}${next.toString() ? `?${next.toString()}` : ''}`);
    },
    [router, pathname, sp]
  );

  useEffect(() => {
    let cancelled = false;
    /**
     * 目的: 会員カテゴリ一覧をAPIから取得して、検索フォームの選択肢として表示する
     * 引数: なし
     * 戻り値: Promise<void>
     * 想定される例外:
     * - ネットワークエラー/タイムアウト
     * - APIが 4xx/5xx を返す
     */
    const load = async () => {
      setLoadingCategories(true);
      setCategoriesError(null);
      try {
        const data = await apiGet<{ items: MemberCategory[] }>(
          '/api/member-categories'
        );
        if (cancelled) return;
        setCategories(Array.isArray(data.items) ? data.items : []);
      } catch (e: any) {
        console.error('[会員カテゴリ取得エラー]', e);
        if (cancelled) return;
        setCategoriesError(
          'カテゴリの取得に失敗しました。しばらく時間をおいて再度お試しください。'
        );
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 目的: 検索ボタン押下でクエリを更新し、一覧側が非同期で再取得する
    // 備考: 絞り込み条件が変わるので page はリセットする
    setParam({
      q: q.trim() ? q.trim() : null,
      categoryId: categoryId.trim() ? categoryId.trim() : null,
      sort: sort === 'recent' ? null : sort,
      gender: gender.trim() ? gender.trim() : null,
      page: null,
    });
  };

  const onCategoryChange = (nextId: string) => {
    setCategoryId(nextId);
    // 目的: カテゴリ変更時は即時に非同期検索を走らせる（ユーザー要件）
    setParam({
      categoryId: nextId.trim() ? nextId.trim() : null,
      page: null,
    });
  };

  const onSortChange = (nextSort: string) => {
    setSort(nextSort);
    // 目的: 並び順変更は即時に反映する
    setParam({
      sort: nextSort === 'recent' ? null : nextSort,
      page: null,
    });
  };

  const onGenderChange = (nextGender: string) => {
    setGender(nextGender);
    // 目的: 性別変更は即時に反映する
    setParam({
      gender: nextGender.trim() ? nextGender.trim() : null,
      page: null,
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      className='w-full rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm backdrop-blur md:p-6'
    >
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4'>
        <label className='w-full'>
          <span className='mb-1 block text-xs font-semibold text-gray-500'>
            名前
          </span>
          <input
            type='text'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='名前で探す'
            className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
          />
        </label>

        <label className='w-full'>
          <span className='mb-1 block text-xs font-semibold text-gray-500'>
            カテゴリ
          </span>
          <select
            value={categoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            aria-label='カテゴリで絞り込む'
            disabled={loadingCategories}
          >
            <option value=''>カテゴリ（全て）</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {categoriesError ? (
            <p className='mt-2 text-xs text-red-600'>{categoriesError}</p>
          ) : null}
        </label>

        <label className='w-full'>
          <span className='mb-1 block text-xs font-semibold text-gray-500'>
            並び順
          </span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            aria-label='並び順'
          >
            <option value='recent'>最近入会した順</option>
            <option value='early'>早く入会した順</option>
          </select>
        </label>

        <label className='w-full'>
          <span className='mb-1 block text-xs font-semibold text-gray-500'>
            性別
          </span>
          <select
            value={gender}
            onChange={(e) => onGenderChange(e.target.value)}
            className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            aria-label='性別'
          >
            <option value=''>性別（全て）</option>
            <option value='female'>女性</option>
            <option value='male'>男性</option>
          </select>
        </label>
      </div>

      <div className='mt-4 flex w-full flex-col gap-3 sm:flex-row sm:justify-end'>
        <button
          type='submit'
          className='w-full rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90 sm:w-auto'
        >
          検索
        </button>
        <Link
          href='/members'
          className='w-full rounded-lg border border-gray-300 px-5 py-2.5 text-center text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 sm:w-auto'
        >
          クリア
        </Link>
      </div>
    </form>
  );
}

